/**
 * ChatMessageBubble - Messaggio chat con layout WhatsApp
 * @updated 2026-01-20
 *
 * MODELLO LOGICO (uguale a tutte le righe):
 * - Inner Actions + Contenuto + Fast Actions
 * - Click → apre modal dettaglio
 *
 * LAYOUT VISIVO (stile WhatsApp):
 * - Messaggi alternati (ricevuti sx, inviati dx)
 * - Inner actions sopra la bubble
 * - Fast actions sotto la bubble
 *
 * ┌─────────────────────────────┐
 * │ [Inner Actions]             │  ← sopra (star, avatar)
 * ├─────────────────────────────┤
 * │   ╭─────────────────╮       │
 * │   │ Contenuto msg   │       │  ← bubble (sx o dx)
 * │   │ 10:30           │       │
 * │   ╰─────────────────╯       │
 * ├─────────────────────────────┤
 * │ [Fast Actions]              │  ← sotto
 * └─────────────────────────────┘
 */

import React, { useState, useMemo } from 'react';
import Markdown from 'react-markdown';
import { Star, Link2, Copy, Check } from 'lucide-react';
import { PrimaryAction, EntityAvatar, type FastActionType } from '../common/base';
import { FastActionsTrigger } from '../actions/FastActions';
import { STANDARD_FAST_ACTIONS } from '../../types/row.types';
import type { ChatMessage, ChatAttachment } from '@business-assistant/shared';
import { FileAttachment, type FileAttachmentData } from './FileAttachment';

// =============================================================================
// TYPES
// =============================================================================

interface ChatMessageBubbleProps {
  message: ChatMessage;
  /** Click sulla riga → apre modal dettaglio */
  onClick?: (message: ChatMessage) => void;
  /** Fast action triggered */
  onAction?: (action: string, message: ChatMessage) => void;
  /** Inner action: favorite toggled */
  onFavoriteChange?: (messageId: string, favorite: boolean) => void;
  /** Numero connessioni esistenti */
  connectionCount?: number;
  /** Compact mode (nascondi actions fino a hover) */
  compact?: boolean;
  /** Click on file attachment to preview */
  onFileClick?: (file: FileAttachmentData) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ChatMessageBubble({
  message,
  onClick,
  onAction,
  onFavoriteChange,
  connectionCount = 0,
  compact = false,
  onFileClick,
}: ChatMessageBubbleProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [copied, setCopied] = useState(false);

  // Determina direzione messaggio
  const isUser = message.role === 'user';
  const senderName = isUser ? 'Tu' : 'Marta';
  const showActions = !compact || isHovered;

  // Click handler - apre modal
  const handleClick = () => {
    onClick?.(message);
  };

  // Fast action handler
  const handleFastAction = (action: FastActionType) => {
    onAction?.(action, message);
  };

  // Copy to clipboard handler
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div
      style={{
        ...styles.container,
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        alignItems: isUser ? 'flex-end' : 'flex-start',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* === INNER ACTIONS (sopra) === */}
      <div
        style={{
          ...styles.innerActions,
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          flexDirection: isUser ? 'row' : 'row-reverse',
          opacity: showActions ? 1 : 0,
          visibility: showActions ? 'visible' : 'hidden',
          pointerEvents: showActions ? 'auto' : 'none',
        }}
      >
        {/* Fast actions trigger - position invertita per messaggi ricevuti */}
        <FastActionsTrigger
          entityId={message.id}
          onAction={(action) => handleFastAction(action as FastActionType)}
          size="sm"
          position={isUser ? 'left' : 'right'}
        />
        {/* Copy button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCopy();
          }}
          title={copied ? 'Copied!' : 'Copy message'}
          style={{
            padding: '4px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: copied ? 'var(--success, #22c55e)' : 'var(--text-muted, #9ca3af)',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '4px',
            transition: 'color 0.15s ease',
          }}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
        <PrimaryAction
          type="star"
          active={isFavorite}
          size={16}
          onChange={(favorite) => {
            setIsFavorite(favorite);
            onFavoriteChange?.(message.id, favorite);
          }}
        />
        <EntityAvatar
          items={{ id: message.id, name: senderName, type: 'person' }}
          type="person"
          size={20}
        />
        {connectionCount > 0 && (
          <span style={styles.connectionBadge}>
            <Link2 size={10} />
            {connectionCount}
          </span>
        )}
      </div>

      {/* === CONTENUTO (bubble) === */}
      <div
        style={{
          ...styles.bubble,
          ...(isUser ? styles.bubbleUser : styles.bubbleAssistant),
          boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.12)' : '0 1px 2px rgba(0,0,0,0.05)',
        }}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      >
        {/* Transcribed badge */}
        {message.transcribed && (
          <span style={styles.transcribedBadge}>trascritto</span>
        )}

        {/* File attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div style={styles.attachments}>
            {message.attachments.map((att) => (
              <FileAttachment
                key={att.id}
                file={{
                  id: att.id,
                  filename: att.name,
                  mimeType: att.type,
                  size: att.size,
                  url: att.url,
                }}
                onClick={onFileClick}
                compact={true}
                isUser={isUser}
              />
            ))}
          </div>
        )}

        {/* Testo messaggio con rendering Markdown */}
        {message.content && (
          <div style={styles.content} className={isUser ? 'markdown-user' : 'markdown-assistant'}>
            <Markdown
              components={{
                // Customize heading sizes for chat bubbles
                h1: ({ children }) => <h4 style={styles.mdHeading}>{children}</h4>,
                h2: ({ children }) => <h4 style={styles.mdHeading}>{children}</h4>,
                h3: ({ children }) => <h5 style={styles.mdHeading}>{children}</h5>,
                h4: ({ children }) => <h5 style={styles.mdHeading}>{children}</h5>,
                // Lists
                ul: ({ children }) => <ul style={styles.mdList}>{children}</ul>,
                ol: ({ children }) => <ol style={styles.mdList}>{children}</ol>,
                li: ({ children }) => <li style={styles.mdListItem}>{children}</li>,
                // Paragraphs
                p: ({ children }) => <p style={styles.mdParagraph}>{children}</p>,
                // Code
                code: ({ children, className }) => {
                  const isInline = !className;
                  return isInline ? (
                    <code style={{
                      ...styles.mdInlineCode,
                      background: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                    }}>{children}</code>
                  ) : (
                    <code style={styles.mdBlockCode}>{children}</code>
                  );
                },
                pre: ({ children }) => <pre style={styles.mdPre}>{children}</pre>,
                // Strong/Bold
                strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                // Links
                a: ({ children, href }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{
                    color: isUser ? '#e0d4fc' : '#8b5cf6',
                    textDecoration: 'underline',
                  }}>{children}</a>
                ),
              }}
            >
              {message.content}
            </Markdown>
          </div>
        )}

        {/* Timestamp */}
        <span style={{
          ...styles.timestamp,
          color: isUser ? 'rgba(255,255,255,0.7)' : '#9ca3af',
        }}>
          {new Date(message.timestamp).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>

    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: '16px',
    maxWidth: '85%',
  },

  // Inner actions (sopra la bubble)
  innerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
    transition: 'opacity 0.15s ease',
  },

  // Bubble comune
  bubble: {
    padding: '10px 14px',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s ease',
    position: 'relative' as const,
  },

  // Bubble utente (destra, viola)
  bubbleUser: {
    background: 'var(--brand, #8b5cf6)',
    color: '#fff',
    borderBottomRightRadius: '4px',
  },

  // Bubble assistente (sinistra, grigio)
  bubbleAssistant: {
    background: '#f3f4f6',
    color: '#1f2937',
    borderBottomLeftRadius: '4px',
  },

  content: {
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.5,
    wordBreak: 'break-word' as const,
  },

  // Markdown-specific styles
  mdHeading: {
    margin: '8px 0 4px 0',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.3,
  } as React.CSSProperties,

  mdParagraph: {
    margin: '0 0 8px 0',
    lineHeight: 1.5,
  } as React.CSSProperties,

  mdList: {
    margin: '4px 0 8px 0',
    paddingLeft: '20px',
  } as React.CSSProperties,

  mdListItem: {
    margin: '2px 0',
    lineHeight: 1.4,
  } as React.CSSProperties,

  mdInlineCode: {
    padding: '1px 4px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '13px',
  } as React.CSSProperties,

  mdBlockCode: {
    display: 'block',
    padding: '8px 12px',
    borderRadius: '6px',
    background: 'rgba(0,0,0,0.08)',
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowX: 'auto' as const,
    whiteSpace: 'pre-wrap' as const,
  } as React.CSSProperties,

  mdPre: {
    margin: '8px 0',
    borderRadius: '6px',
    overflow: 'hidden',
  } as React.CSSProperties,

  timestamp: {
    display: 'block',
    fontSize: '11px',
    marginTop: '4px',
    textAlign: 'right' as const,
  },

  transcribedBadge: {
    display: 'inline-block',
    padding: '2px 6px',
    marginBottom: '6px',
    borderRadius: '4px',
    background: 'rgba(139, 92, 246, 0.2)',
    color: '#8b5cf6',
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  connectionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(139, 92, 246, 0.1)',
    color: '#8b5cf6',
    fontSize: '10px',
    fontWeight: 600,
  } as React.CSSProperties,

  attachments: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginBottom: '8px',
  },
};
